import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  Bell,
  BriefcaseBusiness,
  Building2,
  ChevronDown,
  ChevronRight,
  Cog,
  Funnel,
  House,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  User,
  Users,
  X,
  Apple,
  Smartphone,
  BellRing,
} from "lucide-react";
import {
  AUTH_LAST_ACTIVITY_KEY,
  AUTH_SESSION_KEY,
  SESSION_IDLE_TIMEOUT_MS,
  getLastAuthActivity,
  getUsuarioLogado,
  logout,
  markAuthActivity,
  setLogoutReason,
} from "../services/LoginService";
import ThemeToggle from "./theme-toggle";
import Button from "./ui/Button";
import Avatar, { AvatarBadge, AvatarFallback, AvatarImage, getAvatarInitials } from "./ui/Avatar";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/DropdownMenu";
import { cn } from "../lib/utils";
import { useAppAlert } from "../context/alert-context";
import NotificationCenter from "./NotificationCenter";
import {
  construirUrlWebSocketNotificacoes,
  listarNotificacoes,
  marcarNotificacaoComoLida,
  marcarTodasNotificacoesComoLidas,
  obterContagemNaoLidas,
  publishClientNotificationCreated,
  isPushSupported,
  obterEstadoPushAtual,
  ativarWebPush,
} from "../services/notificationService";
import {
  DASHBOARD_REFRESH_STATE_EVENT,
  requestDashboardRefresh,
} from "../services/dashboardRefreshService";
import {
  PAGE_FILTERS_STATE_EVENT,
  getPageFiltersState,
  requestPageFiltersOpen,
} from "../services/pageFiltersService";
import { BREADCRUMB_ENTITY_EVENT, getBreadcrumbEntity } from "../services/breadcrumbService";
import { DASHBOARD_TABS, resolveDashboardTab } from "./DashboardTabs";

function NavItem({ item, active, collapsed = false, onClick, sub = false, className }) {
  const Icon = item.icon;

  return (
    <Link
      to={item.path}
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      className={cn(
        "group relative flex items-center gap-3 rounded-[9px] font-bold transition-all duration-200",
        // Filho do grupo: um degrau menor que o pai, para a hierarquia se ler sem recuo extra no
        // modo so-icone, onde nao ha espaco para recuar.
        sub
          ? "h-[38px] text-[12px] normal-case tracking-normal"
          : "h-[46px] text-sm uppercase tracking-wide",
        collapsed ? "w-12 justify-center px-0" : "px-3.5",
        active
          // A aba ativa sangra para a direita ate encostar na borda da sidebar: o box-shadow
          // preenche exatamente o padding do <nav>. E a assinatura visual do app.flamex.
          // Usa sidebar-primary (amarelo vivo fixo), nao gold: a sidebar e escura nos dois temas.
          ? cn(
              "rounded-r-none bg-sidebar-primary text-sidebar-primary-foreground",
              collapsed
                ? "shadow-[14px_0_0_0_hsl(var(--sidebar-primary))]"
                : "shadow-[10px_0_0_0_hsl(var(--sidebar-primary))]"
            )
          : "text-sidebar-muted hover:-translate-y-px hover:bg-sidebar-accent hover:text-sidebar-foreground",
        className
      )}
    >
      <Icon className={cn("shrink-0", sub ? "h-4 w-4" : "h-5 w-5")} />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </Link>
  );
}

/**
 * Item de menu com filhos (hoje so o Dashboard, com as abas dele). O pai continua sendo um link:
 * um clique leva para a tela, como antes de o grupo existir. A seta ao lado abre a lista para ir
 * direto na aba desejada.
 *
 * No modo so-icone (76px), os filhos saem do trilho e aparecem em um menu flutuante ao passar o
 * mouse ou focar o item pai. O portal evita que o menu seja cortado pelo scroll da navegacao.
 */
function NavGroup({ item, collapsed, open, onToggle, isChildActive, onNavigate }) {
  const childActive = item.children.some(isChildActive);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutPosition, setFlyoutPosition] = useState({ left: 0, top: 0 });
  const triggerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const cancelFlyoutClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const showFlyout = () => {
    if (!collapsed || !triggerRef.current) return;
    cancelFlyoutClose();
    const rect = triggerRef.current.getBoundingClientRect();
    const estimatedHeight = 54 + item.children.length * 42;
    setFlyoutPosition({
      // O contêiner começa 2px dentro do gatilho. O padding do flyout preserva o afastamento
      // visual de 10px e, ao mesmo tempo, cria uma ponte invisível sob o ponteiro entre os dois.
      left: rect.right - 2,
      top: Math.max(12, Math.min(rect.top, window.innerHeight - estimatedHeight - 12)),
    });
    setFlyoutOpen(true);
  };

  const scheduleFlyoutClose = () => {
    cancelFlyoutClose();
    closeTimerRef.current = window.setTimeout(() => setFlyoutOpen(false), 220);
  };

  useEffect(
    () => () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!collapsed) setFlyoutOpen(false);
  }, [collapsed]);

  const flyout =
    collapsed && flyoutOpen
      ? createPortal(
          <div
            onPointerEnter={cancelFlyoutClose}
            onPointerLeave={scheduleFlyoutClose}
            onFocus={cancelFlyoutClose}
            onBlur={scheduleFlyoutClose}
            onKeyDown={(event) => {
              if (event.key === "Escape") setFlyoutOpen(false);
            }}
            // 12px de padding: 2px sobrepoem o gatilho e os 10px restantes mantêm o respiro
            // visual. Toda essa faixa participa do hover, eliminando a zona morta.
            className="fixed z-[70] w-[236px] pl-3"
            style={flyoutPosition}
          >
            <div
              role="menu"
              aria-label={item.label}
              className="w-[224px] rounded-xl border border-sidebar-border bg-sidebar p-2.5 text-sidebar-foreground shadow-2xl shadow-black/40 animate-in fade-in slide-in-from-left-1 duration-150"
            >
              <p className="px-2.5 pb-2 pt-1 text-[10px] font-bold uppercase tracking-[0.14em] text-sidebar-muted">
                {item.label}
              </p>
              <div className="flex flex-col gap-1">
                {item.children.map((child) => {
                  const ChildIcon = child.icon;
                  const active = isChildActive(child);
                  return (
                    <Link
                      key={child.path}
                      to={child.path}
                      role="menuitem"
                      onClick={() => {
                        setFlyoutOpen(false);
                        onNavigate();
                      }}
                      className={cn(
                        "flex h-10 items-center gap-2.5 rounded-lg px-2.5 text-xs font-semibold normal-case tracking-normal transition-colors",
                        active
                          ? "bg-sidebar-primary text-sidebar-primary-foreground"
                          : "text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-primary"
                      )}
                    >
                      <ChildIcon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{child.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div
      ref={triggerRef}
      className="flex flex-col gap-1"
      onPointerEnter={showFlyout}
      onPointerLeave={scheduleFlyoutClose}
      onFocus={showFlyout}
      onBlur={scheduleFlyoutClose}
    >
      <div className={cn("flex items-center", collapsed ? "justify-center" : "gap-1")}>
        <NavItem
          item={item}
          // Com a lista aberta quem recebe o destaque e o filho: dois realces na mesma coluna
          // brigariam entre si.
          active={childActive && (collapsed || !open)}
          collapsed={collapsed}
          onClick={onNavigate}
          className={collapsed ? undefined : "flex-1"}
        />
        {!collapsed && (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-label={`${open ? "Recolher" : "Expandir"} ${item.label}`}
            className="flex h-[46px] w-8 shrink-0 items-center justify-center rounded-[9px] text-sidebar-muted transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-primary"
          >
            <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", open && "rotate-180")} />
          </button>
        )}
      </div>

      {!collapsed && open && (
        <div className="flex flex-col gap-1 pl-3">
          {item.children.map((child) => (
            <NavItem
              key={child.path}
              item={child}
              active={isChildActive(child)}
              collapsed={false}
              onClick={onNavigate}
              sub
            />
          ))}
        </div>
      )}
      {flyout}
    </div>
  );
}

function isIosDevice() {
  if (typeof navigator === "undefined") {
    return false;
  }
  const isIosUserAgent = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isIpadOnMac = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return isIosUserAgent || isIpadOnMac;
}

const SIDEBAR_STORAGE_KEY = "flamex-sidebar";

// entity: nome publicado pela página aberta (services/breadcrumbService). Vale só se for da rota atual.
function buildBreadcrumbs(pathname, entity = {}) {
  const inicio = { label: "Dashboard", to: "/dashboard" };
  const parceiros = { label: "Parceiros", to: "/parceiros" };
  const usuarios = { label: "Usuarios", to: "/usuarios" };

  if (pathname === "/hub") return [{ label: "Início" }];
  if (pathname === "/dashboard") return [{ label: "Dashboard" }];
  if (pathname === "/parceiros") return [inicio, { label: "Parceiros" }];
  // Parceiro pelo nome: Parceiros › Nome, e na vida do agente o nome vira o link de volta aos detalhes.
  const parceiroMatch = pathname.match(/^\/parceiros\/([^/]+)(\/vida-agente)?$/);
  if (parceiroMatch) {
    const parceiroPath = `/parceiros/${parceiroMatch[1]}`;
    const nome = entity?.path === parceiroPath && entity?.label ? entity.label : "";
    if (parceiroMatch[2]) {
      return [inicio, parceiros, { label: nome || "Parceiro", to: parceiroPath, long: true }, { label: "Vida do agente" }];
    }
    return [inicio, parceiros, { label: nome || "Detalhes do parceiro", long: true }];
  }
  if (pathname === "/tarefas") return [inicio, { label: "Tarefas" }];
  if (pathname === "/usuarios") return [inicio, { label: "Usuarios" }];
  if (/^\/usuarios\/[^/]+$/.test(pathname)) return [inicio, usuarios, { label: "Detalhes do usuario" }];
  if (pathname === "/configuracoes-atividades") return [inicio, { label: "Configurações" }];
  if (pathname === "/integracao-join") return [inicio, { label: "Integracao Join" }];
  if (pathname === "/relatorios/comissao-repasse") return [inicio, { label: "Repasse" }];
  if (pathname === "/relatorios/churn") return [inicio, { label: "Churn" }];
  if (pathname === "/meu-perfil") return [inicio, { label: "Meu perfil" }];
  return [inicio];
}

const MAX_NOTIFICATIONS_IN_MEMORY = 40;

function mergeNotifications(current, incoming) {
  const incomingList = Array.isArray(incoming) ? incoming : [incoming];
  const map = new Map();

  [...incomingList, ...current].forEach((notification) => {
    if (!notification?.id) {
      return;
    }
    map.set(notification.id, notification);
  });

  return Array.from(map.values())
    .sort((left, right) => new Date(right?.createdAt || 0) - new Date(left?.createdAt || 0))
    .slice(0, MAX_NOTIFICATIONS_IN_MEMORY);
}

function applyReadState(current, notificationId, readAt) {
  return current.map((notification) =>
    notification?.id === notificationId
      ? { ...notification, read: true, readAt: readAt || notification.readAt || new Date().toISOString() }
      : notification
  );
}

function applyReadAllState(current, readAt) {
  return current.map((notification) => ({
    ...notification,
    read: true,
    readAt: readAt || notification.readAt || new Date().toISOString(),
  }));
}

function resolveAlertVariant(severity) {
  switch (String(severity || "").toUpperCase()) {
    case "CRITICAL":
      return "error";
    case "WARNING":
      return "warning";
    case "SUCCESS":
      return "success";
    default:
      return "info";
  }
}

const DEFAULT_DASHBOARD_REFRESH_STATE = {
  visible: false,
  loading: false,
  countdown: null,
  countdownLabel: "",
};

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const alert = useAppAlert();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const [expandedDesktop, setExpandedDesktop] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "expanded";
    } catch (error) {
      return false;
    }
  });
  const [usuario, setUsuario] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [webPushPromptVisible, setWebPushPromptVisible] = useState(false);
  const [showPushTutorialModal, setShowPushTutorialModal] = useState(false);
  const [isPushActivating, setIsPushActivating] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationReadStatus, setNotificationReadStatus] = useState("UNREAD");
  const [notificationDateFrom, setNotificationDateFrom] = useState("");
  const [notificationDateTo, setNotificationDateTo] = useState("");
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [websocketConnected, setWebsocketConnected] = useState(false);
  const [dashboardRefreshState, setDashboardRefreshState] = useState(DEFAULT_DASHBOARD_REFRESH_STATE);
  const [pageFilters, setPageFilters] = useState(() => getPageFiltersState());
  const [breadcrumbEntity, setBreadcrumbEntity] = useState(() => getBreadcrumbEntity());
  const idleTimerRef = useRef(null);
  const idleLogoutRef = useRef(false);
  const websocketRef = useRef(null);
  const websocketReconnectRef = useRef(null);
  const sessionClosingRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const user = await getUsuarioLogado();
        setUsuario(user);
        setIsAdmin(String(user?.cargo || "").trim().toLowerCase() === "admin");
      } catch {
        setUsuario(null);
        setIsAdmin(false);
      }
    })();
  }, []);

  const checkWebPushState = useCallback(async (userObj) => {
    const activeUser = userObj || usuario;
    if (!activeUser?.id) {
      setWebPushPromptVisible(false);
      return;
    }

    const dismissedUntil = localStorage.getItem("flamex_webpush_prompt_dismissed_until");
    if (dismissedUntil && Number(dismissedUntil) > Date.now()) {
      setWebPushPromptVisible(false);
      return;
    }

    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isStandaloneMode = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

    if (isIOSDevice) {
      if (!isStandaloneMode) {
        setWebPushPromptVisible(true);
        return;
      }
    }

    if (!isPushSupported()) {
      setWebPushPromptVisible(false);
      return;
    }

    try {
      const state = await obterEstadoPushAtual();
      const needsPush = state.permission !== "granted" || !state.subscribed;
      setWebPushPromptVisible(needsPush);
    } catch (err) {
      console.error("Erro ao verificar status do WebPush:", err);
      setWebPushPromptVisible(false);
    }
  }, [usuario]);

  useEffect(() => {
    if (usuario?.id) {
      checkWebPushState(usuario);
    } else {
      setWebPushPromptVisible(false);
    }
  }, [usuario, checkWebPushState]);

  const handleActivatePush = async () => {
    setIsPushActivating(true);
    try {
      await ativarWebPush();
      alert.success("Notificações ativadas", "Você começará a receber alertas em tempo real neste dispositivo.");
      setWebPushPromptVisible(false);
    } catch (err) {
      console.error("Erro ao ativar WebPush:", err);
      alert.error("Falha ao ativar notificações", err?.message || "Não foi possível concluir a ativação.");
    } finally {
      setIsPushActivating(false);
    }
  };

  const handleDismissPushPrompt = () => {
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    localStorage.setItem("flamex_webpush_prompt_dismissed_until", String(Date.now() + oneWeek));
    setWebPushPromptVisible(false);
  };

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleDashboardRefreshState = (event) => {
      setDashboardRefreshState({
        ...DEFAULT_DASHBOARD_REFRESH_STATE,
        ...(event?.detail || {}),
      });
    };

    window.addEventListener(DASHBOARD_REFRESH_STATE_EVENT, handleDashboardRefreshState);
    return () => {
      window.removeEventListener(DASHBOARD_REFRESH_STATE_EVENT, handleDashboardRefreshState);
    };
  }, []);

  // Nome do registro aberto (ex.: parceiro) no lugar do rótulo genérico do breadcrumb.
  useEffect(() => {
    const handleBreadcrumbEntity = (event) => setBreadcrumbEntity(event?.detail || { path: "", label: "" });
    window.addEventListener(BREADCRUMB_ENTITY_EVENT, handleBreadcrumbEntity);
    // A página pode ter publicado antes desta assinatura (efeito de filho roda antes do pai).
    setBreadcrumbEntity(getBreadcrumbEntity());
    return () => window.removeEventListener(BREADCRUMB_ENTITY_EVENT, handleBreadcrumbEntity);
  }, []);

  // Filtros da página aberta (dashboard, listas...): o funil aparece quando a página registra
  // filtros e mostra quantos estão aplicados.
  useEffect(() => {
    const handlePageFiltersState = (event) => {
      setPageFilters({
        visible: Boolean(event?.detail?.visible),
        count: Number(event?.detail?.count) || 0,
      });
    };

    window.addEventListener(PAGE_FILTERS_STATE_EVENT, handlePageFiltersState);
    // A página pode ter publicado antes desta assinatura (efeito de filho roda antes do pai).
    setPageFilters(getPageFiltersState());
    return () => {
      window.removeEventListener(PAGE_FILTERS_STATE_EVENT, handlePageFiltersState);
    };
  }, []);

  const loadNotifications = useCallback(
    async ({
      readStatus = notificationReadStatus,
      dateFrom = notificationDateFrom,
      dateTo = notificationDateTo,
      silent = false,
    } = {}) => {
      if (!usuario?.id) {
        setNotifications([]);
        setNotificationUnreadCount(0);
        return;
      }

      if (!silent) {
        setNotificationsLoading(true);
      }

      try {
        const [pageData, unreadCount] = await Promise.all([
          listarNotificacoes({ page: 0, size: 50, readStatus, dateFrom, dateTo }),
          obterContagemNaoLidas(),
        ]);

        const content = Array.isArray(pageData?.content) ? pageData.content : [];
        setNotifications(content);
        setNotificationUnreadCount(unreadCount);
      } catch (error) {
        if (!silent) {
          const message = error?.response?.data?.message || "Não foi possível carregar as notificações";
          alert.error("Notificações", message, { position: "top-right", duration: 2800 });
        }
      } finally {
        if (!silent) {
          setNotificationsLoading(false);
        }
      }
    },
    [alert, notificationDateFrom, notificationDateTo, notificationReadStatus, usuario?.id]
  );

  useEffect(() => {
    if (!usuario?.id) {
      setNotifications([]);
      setNotificationUnreadCount(0);
      return;
    }

    loadNotifications({ readStatus: notificationReadStatus, silent: true });
  }, [loadNotifications, notificationReadStatus, usuario?.id]);

  useEffect(() => {
    if (!usuario?.id) {
      sessionClosingRef.current = false;
      if (websocketReconnectRef.current) {
        window.clearTimeout(websocketReconnectRef.current);
        websocketReconnectRef.current = null;
      }
      if (websocketRef.current) {
        websocketRef.current.close(1000, "Usuário não autenticado");
        websocketRef.current = null;
      }
      setWebsocketConnected(false);
      return undefined;
    }

    let active = true;
    sessionClosingRef.current = false;

    const hasLocalAuthSession = () => localStorage.getItem(AUTH_SESSION_KEY) === "1";

    const clearSocketReferences = () => {
      if (websocketReconnectRef.current) {
        window.clearTimeout(websocketReconnectRef.current);
        websocketReconnectRef.current = null;
      }
      if (websocketRef.current) {
        try {
          websocketRef.current.close(1000, "Sessao encerrada");
        } catch {
          // Sem acao adicional.
        }
        websocketRef.current = null;
      }
      setWebsocketConnected(false);
    };

    const handleSessionExpired = async () => {
      if (!active || sessionClosingRef.current) {
        return false;
      }
      sessionClosingRef.current = true;
      clearSocketReferences();
      localStorage.removeItem(AUTH_SESSION_KEY);
      localStorage.removeItem(AUTH_LAST_ACTIVITY_KEY);
      setUsuario(null);
      setIsAdmin(false);
      setNotifications([]);
      setNotificationUnreadCount(0);
      alert.info("Sessao encerrada", "Sua sessao expirou ou foi encerrada em outro lugar.", {
        position: "top-right",
        duration: 3200,
      });
      navigate("/", { replace: true });
      return false;
    };

    const validateSessionBeforeReconnect = async () => {
      if (!hasLocalAuthSession()) {
        return false;
      }

      try {
        await getUsuarioLogado();
        return true;
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          await handleSessionExpired();
          return false;
        }
        return true;
      }
    };

    const scheduleReconnect = () => {
      if (!active || sessionClosingRef.current || websocketReconnectRef.current || !hasLocalAuthSession()) {
        return;
      }
      websocketReconnectRef.current = window.setTimeout(async () => {
        websocketReconnectRef.current = null;
        const sessionStillValid = await validateSessionBeforeReconnect();
        if (!sessionStillValid) {
          return;
        }
        connectWebSocket();
      }, 5000);
    };

    const handleSocketMessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      const payload = data?.payload;
      if (!data?.type) {
        return;
      }

      if (data.type === "notification.created" && payload?.id) {
        setNotifications((current) => {
          const alreadyExists = current.some((notification) => notification?.id === payload.id);
          if (!alreadyExists && !payload.read) {
            setNotificationUnreadCount((count) => count + 1);
          }
          return mergeNotifications(current, payload);
        });
        publishClientNotificationCreated(payload);
        alert[resolveAlertVariant(payload.severity)]?.(
          payload.title || payload.eventLabel || "Nova notificacao",
          payload.message || "Voce recebeu uma nova notificacao.",
          { position: "top-right", duration: 3600 }
        );
        return;
      }

      if (data.type === "notification.read" && payload?.notificationId) {
        setNotifications((current) => {
          const target = current.find((notification) => notification?.id === payload.notificationId);
          if (target && !target.read) {
            setNotificationUnreadCount((count) => Math.max(count - 1, 0));
          }
          return applyReadState(current, payload.notificationId, payload.readAt);
        });
        return;
      }

      if (data.type === "notification.read-all") {
        setNotifications((current) => applyReadAllState(current, payload?.readAt));
        setNotificationUnreadCount(0);
      }
    };

    const connectWebSocket = () => {
      if (!active || sessionClosingRef.current || !hasLocalAuthSession()) {
        return;
      }
      if (
        websocketRef.current &&
        (websocketRef.current.readyState === WebSocket.OPEN
          || websocketRef.current.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      try {
        const socket = new WebSocket(construirUrlWebSocketNotificacoes());
        websocketRef.current = socket;

        socket.onopen = () => {
          if (!active) {
            socket.close(1000, "Layout desmontado");
            return;
          }
          setWebsocketConnected(true);
        };

        socket.onmessage = handleSocketMessage;

        socket.onerror = () => {
          try {
            socket.close();
          } catch {
            // Sem acao adicional.
          }
        };

        socket.onclose = () => {
          if (websocketRef.current === socket) {
            websocketRef.current = null;
          }
          setWebsocketConnected(false);
          if (!hasLocalAuthSession()) {
            return;
          }
          scheduleReconnect();
        };
      } catch {
        setWebsocketConnected(false);
        scheduleReconnect();
      }
    };

    connectWebSocket();

    return () => {
      active = false;
      if (websocketReconnectRef.current) {
        window.clearTimeout(websocketReconnectRef.current);
        websocketReconnectRef.current = null;
      }
      if (websocketRef.current) {
        websocketRef.current.close(1000, "Layout desmontado");
        websocketRef.current = null;
      }
      setWebsocketConnected(false);
    };
  }, [alert, usuario?.id]);

  useEffect(() => {
    if (!usuario) {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      idleLogoutRef.current = false;
      return undefined;
    }

    const clearIdleTimer = () => {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };

    const executeIdleLogout = async () => {
      if (idleLogoutRef.current) return;
      idleLogoutRef.current = true;
      sessionClosingRef.current = true;
      clearIdleTimer();
      setLogoutReason("INATIVIDADE");
      setUsuario(null);
      setIsAdmin(false);
      try {
        await logout();
      } catch {
        // A sessao pode ja ter expirado no backend; o estado local ainda precisa ser limpo.
      } finally {
        navigate("/", { replace: true });
      }
    };

    const scheduleIdleTimer = () => {
      clearIdleTimer();
      const lastActivity = getLastAuthActivity() || Date.now();
      const remainingMs = SESSION_IDLE_TIMEOUT_MS - (Date.now() - lastActivity);
      if (remainingMs <= 0) {
        executeIdleLogout();
        return;
      }
      idleTimerRef.current = window.setTimeout(executeIdleLogout, remainingMs);
    };

    const registerInteraction = () => {
      if (idleLogoutRef.current) return;
      markAuthActivity();
      scheduleIdleTimer();
    };

    const handleVisibilityOrFocus = () => {
      if (idleLogoutRef.current) return;
      scheduleIdleTimer();
    };

    const handleStorage = (event) => {
      if (event.key === AUTH_LAST_ACTIVITY_KEY) {
        scheduleIdleTimer();
        return;
      }

      if (event.key === AUTH_SESSION_KEY && event.newValue !== "1") {
        clearIdleTimer();
        setUsuario(null);
        setIsAdmin(false);
        navigate("/", { replace: true });
      }
    };

    const interactionEvents = ["pointerdown", "keydown", "wheel", "touchstart"];
    interactionEvents.forEach((eventName) => window.addEventListener(eventName, registerInteraction, { passive: true }));
    window.addEventListener("focus", handleVisibilityOrFocus);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("storage", handleStorage);

    if (!getLastAuthActivity()) {
      markAuthActivity();
    }
    scheduleIdleTimer();

    return () => {
      clearIdleTimer();
      interactionEvents.forEach((eventName) => window.removeEventListener(eventName, registerInteraction));
      window.removeEventListener("focus", handleVisibilityOrFocus);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.removeEventListener("storage", handleStorage);
    };
  }, [alert, navigate, usuario]);

  const menuItems = useMemo(
    () => [
      { path: "/hub", icon: House, label: "Início" },
      {
        path: "/dashboard",
        icon: BarChart3,
        label: "Dashboard",
        // As abas do dashboard viram filhos do menu: dá para cair direto na aba desejada. Comissões
        // está entre elas e por isso não aparece mais solta no primeiro nível.
        children: DASHBOARD_TABS.map((tab) => ({
          value: tab.value,
          path: tab.to,
          icon: tab.icon,
          label: tab.label,
        })),
      },
      { path: "/parceiros", icon: Building2, label: "Parceiros" },
      { path: "/tarefas", icon: BriefcaseBusiness, label: "Tarefas" },
      { path: "/usuarios", icon: Users, label: "Usuários", adminOnly: true },
      { path: "/configuracoes-atividades", icon: Cog, label: "Configurações", adminOnly: true },
      { path: "/integracao-join", icon: RefreshCw, label: "Integração Join", adminOnly: true },
    ],
    []
  );

  const visibleItems = menuItems.filter((item) => !item.adminOnly || isAdmin);
  const activeDashboardTab = resolveDashboardTab(location);
  // Na sidebar expandida, grupos com filhos ja iniciam abertos. A escolha manual do usuario
  // continua valendo ate a pagina ser recarregada.
  const isGroupOpen = (item) => openGroups[item.path] ?? true;
  const toggleGroup = (item) =>
    setOpenGroups((prev) => ({ ...prev, [item.path]: !isGroupOpen(item) }));
  const showLabels = expandedDesktop || sidebarOpen;
  const desktopWidth = expandedDesktop ? "lg:w-[268px]" : "lg:w-[76px]";
  const contentOffset = expandedDesktop ? "lg:pl-[268px]" : "lg:pl-[76px]";
  const breadcrumbs = buildBreadcrumbs(location.pathname, breadcrumbEntity);

  const toggleDesktopSidebar = useCallback(() => {
    setExpandedDesktop((previous) => {
      const next = !previous;
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? "expanded" : "collapsed");
      } catch (error) {
        /* preferencia e opcional */
      }
      return next;
    });
  }, []);
  const usuarioOnline = typeof usuario?.sessaoAtiva === "boolean" ? usuario.sessaoAtiva : Boolean(usuario?.id);

  const handleLogout = async () => {
    sessionClosingRef.current = true;
    setUsuario(null);
    setIsAdmin(false);
    try {
      await logout();
      alert.success("Logout realizado com sucesso", "Sua sessao foi encerrada.", {
        position: "top-right",
        duration: 2200,
      });
    } finally {
      navigate("/", { replace: true });
    }
  };

  const handleOpenNotificationCenter = async () => {
    setNotificationCenterOpen(true);
    await loadNotifications({ readStatus: notificationReadStatus });
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      const updated = await marcarNotificacaoComoLida(notificationId);
      setNotifications((current) => applyReadState(current, notificationId, updated?.readAt));
      setNotificationUnreadCount((current) => Math.max(current - 1, 0));
      if (notificationReadStatus === "UNREAD") {
        setNotifications((current) => current.filter((notification) => notification.id !== notificationId));
      }
    } catch (error) {
      const message = error?.response?.data?.message || "Não foi possível marcar a notificação como lida";
      alert.error("Notificações", message, { position: "top-right", duration: 2600 });
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await marcarTodasNotificacoesComoLidas();
      setNotifications((current) => applyReadAllState(current));
      setNotificationUnreadCount(0);
      if (notificationReadStatus === "UNREAD") {
        setNotifications([]);
      }
    } catch (error) {
      const message = error?.response?.data?.message || "Não foi possível marcar todas como lidas";
      alert.error("Notificações", message, { position: "top-right", duration: 2600 });
    }
  };

  const handleOpenNotification = async (notification) => {
    const payload = notification?.payload || {};
    const taskId =
      notification?.entityType === "TAREFA"
        ? notification?.entityId || payload?.taskId || null
        : payload?.taskId || notification?.entityId || null;
    const route = notification?.route || "/tarefas";

    setNotificationCenterOpen(false);
    if (taskId && route.startsWith("/tarefas")) {
      navigate(route, {
        state: {
          openTaskId: taskId,
          openNotificationId: notification?.id || null,
          openRequestId: Date.now(),
        },
      });
      return;
    }

    navigate(route);
  };

  const handleNotificationTabChange = async (nextReadStatus) => {
    setNotificationReadStatus(nextReadStatus);
    await loadNotifications({
      readStatus: nextReadStatus,
      dateFrom: notificationDateFrom,
      dateTo: notificationDateTo,
    });
  };

  const handleApplyNotificationFilters = async () => {
    await loadNotifications({
      readStatus: notificationReadStatus,
      dateFrom: notificationDateFrom,
      dateTo: notificationDateTo,
    });
  };

  const handleClearNotificationFilters = async () => {
    setNotificationDateFrom("");
    setNotificationDateTo("");
    await loadNotifications({
      readStatus: notificationReadStatus,
      dateFrom: "",
      dateTo: "",
    });
  };

  // overflow-x-clip, não hidden: `hidden` em um eixo força o outro para `auto`, a div raiz vira
  // contêiner de rolagem e o `sticky` da topbar deixa de acompanhar o scroll da janela.
  return (
    <div className="min-h-screen overflow-x-clip">
      {/* ---------------- Sidebar ---------------- */}
      <aside
        className={cn(
          // Sem border-r, como no protótipo: 1px de borda fazia o rail de 76px transbordar
          // (barra de rolagem horizontal) e deixava um risco escuro à direita da aba ativa.
          "fixed inset-y-0 left-0 z-50 flex w-[268px] flex-col bg-sidebar text-sidebar-foreground",
          "transition-[width,transform] duration-200 lg:translate-x-0",
          desktopWidth,
          sidebarOpen ? "translate-x-0 shadow-2xl shadow-black/40" : "-translate-x-full"
        )}
      >
        {/* Marca */}
        <div className="relative flex h-[76px] shrink-0 items-center gap-3 border-b border-sidebar-border px-4">
          <img
            src="/favicon-flamex.svg"
            alt="Flamex"
            className="h-10 w-10 shrink-0 rounded-[9px] object-contain"
          />
          {showLabels && (
            <div className="min-w-0">
              <p className="truncate font-display text-sm font-semibold leading-tight text-sidebar-foreground">
                Flamex
              </p>
              <p className="truncate text-[11px] leading-tight text-sidebar-muted">Comercial</p>
            </div>
          )}

          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="ml-auto grid h-8 w-8 shrink-0 place-items-center rounded-lg text-sidebar-muted transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground lg:hidden"
            aria-label="Fechar menu"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Gatilho colado na borda direita da sidebar */}
          <button
            type="button"
            onClick={toggleDesktopSidebar}
            className="absolute -right-4 top-1/2 z-[3] hidden h-8 w-8 -translate-y-1/2 place-items-center rounded-[4px] bg-sidebar text-sidebar-foreground transition-all hover:scale-105 hover:bg-sidebar-accent lg:grid"
            aria-label={expandedDesktop ? "Recolher menu lateral" : "Expandir menu lateral"}
            title={expandedDesktop ? "Recolher menu lateral" : "Expandir menu lateral"}
          >
            {expandedDesktop ? (
              <PanelLeftClose className="h-[21px] w-[21px]" />
            ) : (
              <PanelLeftOpen className="h-[21px] w-[21px]" />
            )}
          </button>
        </div>

        {/* Navegacao. O padding lateral casa com o box-shadow da aba ativa.
            overflow-x-hidden explicito: com so overflow-y-auto o navegador trata o eixo x como
            auto e qualquer pixel a mais vira barra de rolagem horizontal. */}
        <nav
          className={cn(
            "flex flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden py-6",
            showLabels ? "px-2.5" : "px-3.5"
          )}
        >
          {visibleItems.map((item) => {
            if (item.children) {
              return (
                <NavGroup
                  key={item.path}
                  item={item}
                  collapsed={!showLabels}
                  open={isGroupOpen(item)}
                  onToggle={() => toggleGroup(item)}
                  isChildActive={(child) => child.value === activeDashboardTab}
                  onNavigate={() => setSidebarOpen(false)}
                />
              );
            }

            const active =
              location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
            return (
              <NavItem
                key={item.path}
                item={item}
                active={active}
                collapsed={!showLabels}
                onClick={() => setSidebarOpen(false)}
              />
            );
          })}
        </nav>

        {showLabels && (
          <div className="shrink-0 border-t border-sidebar-border px-3 py-3 text-center">
            <span className="text-[10px] font-medium tracking-wider text-sidebar-muted/70">FLAMEX v1.1</span>
          </div>
        )}
      </aside>

      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/55 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-label="Fechar menu"
        />
      )}

      {/* ---------------- Conteudo ---------------- */}
      {/* Sem transição no padding: animar o recuo recalcula o layout da página inteira (e os
          gráficos) a cada quadro e travava ao expandir a sidebar. Só a sidebar anima. */}
      <div className={cn("min-h-screen", contentOffset)}>
        <header className="sticky top-0 z-30 flex h-[76px] items-center justify-between gap-4 border-b border-border bg-surface/95 px-4 backdrop-blur-xl sm:px-6 lg:px-10">
          <div className="flex min-w-0 items-center gap-3.5">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground lg:hidden"
              aria-label="Abrir menu"
            >
              <Menu className="h-5 w-5" />
            </button>

            {breadcrumbs.length > 1 && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="hidden h-[38px] w-[38px] shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground sm:grid"
                aria-label="Voltar"
                title="Voltar"
              >
                <ArrowLeft className="h-[17px] w-[17px]" />
              </button>
            )}

            <nav
              aria-label="Trilha de navegacao"
              className="min-w-0 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <ol className="flex w-max flex-nowrap items-center gap-[7px] text-xs">
                {breadcrumbs.map((crumb, index) => {
                  const isLast = index === breadcrumbs.length - 1;
                  return (
                    <li key={`${crumb.label}-${index}`} className="flex items-center gap-[7px]">
                      {index > 0 && (
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" aria-hidden="true" />
                      )}
                      {isLast || !crumb.to ? (
                        <span
                          className={cn("whitespace-nowrap font-bold text-foreground", crumb.long && "block max-w-[40vw] truncate sm:max-w-[360px]")}
                          title={crumb.long ? crumb.label : undefined}
                          aria-current="page"
                        >
                          {crumb.label}
                        </span>
                      ) : (
                        <Link
                          to={crumb.to}
                          title={crumb.long ? crumb.label : undefined}
                          className={cn(
                            "whitespace-nowrap font-semibold text-muted-foreground transition-colors hover:text-foreground",
                            crumb.long && "block max-w-[30vw] truncate sm:max-w-[280px]"
                          )}
                        >
                          {crumb.label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {/* Contagem de atualização automática: aparece em qualquer página que publique o estado
                (dashboard, detalhes do parceiro). Cada página limpa o estado ao desmontar. */}
            {dashboardRefreshState.visible ? (
              <div className="relative hidden items-center sm:flex">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => requestDashboardRefresh({ resetTimer: true })}
                  disabled={dashboardRefreshState.loading}
                  className="h-[38px] w-[38px] rounded-lg text-muted-foreground hover:text-foreground"
                  title={dashboardRefreshState.loading ? "Atualizando" : "Atualizar agora"}
                >
                  <RefreshCw className={cn("h-4 w-4", dashboardRefreshState.loading && "animate-spin")} />
                </Button>
                <span className="pointer-events-none absolute left-1/2 top-full min-w-[3rem] -translate-x-1/2 text-center text-[10px] font-medium tabular-nums text-muted-foreground">
                  {dashboardRefreshState.loading ? "--:--" : dashboardRefreshState.countdownLabel || "--:--"}
                </span>
              </div>
            ) : null}

            {pageFilters.visible ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={requestPageFiltersOpen}
                className="relative h-[38px] w-[38px] rounded-lg text-muted-foreground hover:text-foreground"
                title={pageFilters.count > 0 ? `Filtros (${pageFilters.count} aplicados)` : "Filtros"}
                aria-label={pageFilters.count > 0 ? `Filtros, ${pageFilters.count} aplicados` : "Filtros"}
              >
                <Funnel className="h-4 w-4" />
                {pageFilters.count > 0 ? (
                  <span className="absolute -right-1 -top-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-sidebar-primary px-1 text-[10px] font-bold leading-none tabular-nums text-sidebar-primary-foreground ring-2 ring-surface">
                    {pageFilters.count > 99 ? "99+" : pageFilters.count}
                  </span>
                ) : null}
              </Button>
            ) : null}

            <Button
              variant="ghost"
              size="icon"
              onClick={handleOpenNotificationCenter}
              className="relative h-[38px] w-[38px] rounded-lg text-muted-foreground hover:text-foreground"
              title="Notificacoes"
            >
              <Bell className="h-[18px] w-[18px]" />
              {notificationUnreadCount > 0 ? (
                <span className="absolute -right-1 -top-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-badge-danger px-1 text-[10px] font-bold leading-none tabular-nums text-badge-foreground ring-2 ring-surface">
                  {notificationUnreadCount > 99 ? "99+" : notificationUnreadCount}
                </span>
              ) : null}
            </Button>

            <ThemeToggle className="mx-1" />

            <DropdownMenu
              align="end"
              trigger={
                <button
                  type="button"
                  className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-full transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[open]:scale-105"
                  aria-label="Abrir menu do usuario"
                >
                  <Avatar className="h-[38px] w-[38px] rounded-full border-0 bg-primary text-[11px] font-extrabold text-primary-foreground">
                    <AvatarImage src={usuario?.fotoPerfilUrl} alt={usuario?.nome || "Usuario"} />
                    <AvatarFallback>{getAvatarInitials(usuario?.nome || "Usuario")}</AvatarFallback>
                    {usuarioOnline ? <AvatarBadge tone="online" size="sm" /> : null}
                  </Avatar>
                </button>
              }
            >
              <DropdownMenuLabel>
                <div className="flex items-center gap-3">
                  <Avatar className="h-[38px] w-[38px] shrink-0 rounded-full border-0 bg-primary text-[11px] font-extrabold text-primary-foreground">
                    <AvatarImage src={usuario?.fotoPerfilUrl} alt={usuario?.nome || "Usuario"} />
                    <AvatarFallback>{getAvatarInitials(usuario?.nome || "Usuario")}</AvatarFallback>
                  </Avatar>
                  <span className="flex min-w-0 flex-col">
                    <strong className="truncate text-xs font-semibold text-foreground">
                      {usuario?.nome || "Usuario"}
                    </strong>
                    <small className="mt-0.5 truncate text-[9px] uppercase tracking-wide text-muted-foreground">
                      {usuario?.email || "Sem email"}
                    </small>
                  </span>
                </div>
              </DropdownMenuLabel>

              <DropdownMenuItem onClick={() => navigate("/meu-perfil")}>
                <User />
                Meu perfil
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem tone="danger" onClick={handleLogout}>
                <LogOut />
                Sair
              </DropdownMenuItem>
            </DropdownMenu>
          </div>
        </header>

        <main className="min-h-[calc(100vh-76px)]">
          <div className="app-shell">
            {webPushPromptVisible && (
              <div className="mb-5 flex flex-col items-center justify-between gap-4 rounded-2xl border border-gold/25 bg-card p-4 shadow-sm transition-all md:flex-row">
                <div className="flex items-center gap-3.5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gold/25 bg-gold/10 text-gold shadow-inner">
                    <BellRing className="h-5 w-5 animate-pulse" />
                  </div>
                  <div className="text-left">
                    <h4 className="text-sm font-semibold text-foreground">
                      Ative as Notificacoes em Tempo Real!
                    </h4>
                    <p className="mt-0.5 max-w-xl text-xs text-muted-foreground">
                      {isIosDevice()
                        ? "Receba alertas instantaneos de tarefas e inativacoes. Para comecar no iOS, voce precisa adicionar este portal a tela inicial do seu celular."
                        : "Receba avisos instantaneos de novas tarefas, prazos, evidencias e quando parceiros forem inativados por API ou webhook."}
                    </p>
                  </div>
                </div>
                <div className="flex w-full items-center justify-end gap-2.5 md:w-auto">
                  {isIosDevice() ? (
                    <Button variant="gold" size="sm" onClick={() => setShowPushTutorialModal(true)}>
                      <Apple className="h-3.5 w-3.5" />
                      Como Instalar no iPhone
                    </Button>
                  ) : (
                    <Button variant="gold" size="sm" onClick={handleActivatePush} disabled={isPushActivating}>
                      {isPushActivating ? "Ativando..." : "Ativar Notificacoes"}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={handleDismissPushPrompt}>
                    Agora Nao
                  </Button>
                </div>
              </div>
            )}
            <Outlet />
          </div>
        </main>
      </div>

      <NotificationCenter
        isOpen={notificationCenterOpen}
        onClose={() => setNotificationCenterOpen(false)}
        notifications={notifications}
        unreadCount={notificationUnreadCount}
        readStatus={notificationReadStatus}
        loading={notificationsLoading}
        websocketConnected={websocketConnected}
        dateFrom={notificationDateFrom}
        dateTo={notificationDateTo}
        onReadStatusChange={handleNotificationTabChange}
        onDateFromChange={setNotificationDateFrom}
        onDateToChange={setNotificationDateTo}
        onApplyFilters={handleApplyNotificationFilters}
        onClearFilters={handleClearNotificationFilters}
        onRefresh={() => loadNotifications({ readStatus: notificationReadStatus })}
        onMarkRead={handleMarkNotificationRead}
        onMarkAllRead={handleMarkAllNotificationsRead}
        onOpenNotification={handleOpenNotification}
      />

      {showPushTutorialModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm transition-opacity duration-300">
          <div className="bg-card border border-border text-card-foreground rounded-2xl max-w-md w-full overflow-hidden shadow-2xl relative">
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-primary via-flame to-gold" />
            
            <button
              onClick={() => setShowPushTutorialModal(false)}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="p-6">
              <div className="flex items-center gap-3 mb-5 mt-2">
                <div className="h-10 w-10 rounded-xl bg-gold/10 border border-gold/25 flex items-center justify-center text-gold">
                  <Apple className="h-5.5 w-5.5" />
                </div>
                <div className="text-left">
                  <h3 className="font-display font-semibold text-lg text-foreground">
                    Instalar no iPhone / iPad
                  </h3>
                  <p className="text-muted-foreground text-xs">
                    Siga os passos abaixo para transformar o sistema em um aplicativo e receber alertas.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex gap-3 text-left">
                  <div className="h-6 w-6 rounded-full bg-gold/10 border border-gold/25 flex items-center justify-center text-gold text-xs font-bold shrink-0 mt-0.5">
                    1
                  </div>
                  <div>
                    <h5 className="font-semibold text-sm text-foreground">Abra o Compartilhamento</h5>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                      No navegador Safari, clique no botão de <strong>Compartilhar</strong>
                      <span className="inline-flex p-1 bg-muted border border-border rounded-md text-muted-foreground">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" />
                        </svg>
                      </span>
                      na barra de ferramentas (rodapé).
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 text-left">
                  <div className="h-6 w-6 rounded-full bg-gold/10 border border-gold/25 flex items-center justify-center text-gold text-xs font-bold shrink-0 mt-0.5">
                    2
                  </div>
                  <div>
                    <h5 className="font-semibold text-sm text-foreground">Adicione à Tela de Início</h5>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                      Role a lista de opções para baixo e toque em <strong>Adicionar à Tela de Início</strong>
                      <span className="inline-flex p-1 bg-muted border border-border rounded-md text-muted-foreground">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <rect x="3" y="3" width="18" height="18" rx="4" />
                          <line x1="12" y1="8" x2="12" y2="16" />
                          <line x1="8" y1="12" x2="16" y2="12" />
                        </svg>
                      </span>.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 text-left">
                  <div className="h-6 w-6 rounded-full bg-gold/10 border border-gold/25 flex items-center justify-center text-gold text-xs font-bold shrink-0 mt-0.5">
                    3
                  </div>
                  <div>
                    <h5 className="font-semibold text-sm text-foreground">Confirme a Instalação</h5>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Toque em <strong>Adicionar</strong> no canto superior direito para confirmar. O ícone do app aparecerá na tela do seu celular.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 text-left">
                  <div className="h-6 w-6 rounded-full bg-gold/10 border border-gold/25 flex items-center justify-center text-gold text-xs font-bold shrink-0 mt-0.5">
                    4
                  </div>
                  <div>
                    <h5 className="font-semibold text-sm text-foreground">Ative as Notificações</h5>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Abra o novo aplicativo <strong>Flamex</strong> instalado, faça o login novamente. O banner de notificações aparecerá no topo e você poderá ativá-lo com 1 toque!
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowPushTutorialModal(false)}
                  className="w-full px-4 py-2.5 bg-gold hover:bg-gold/90 text-gold-foreground font-semibold text-sm rounded-xl shadow-md transition-all"
                >
                  Entendi, vou fazer isso!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
